<template>
  <div
    :class="['background', {
      'background--home-page': isHomePage,
      'background--home-page-with-title-spacer': isHomePage && withTitleSpacer,
      'background--with-background-image': background,
      'background--product-page': isProductPage,
      'background--funnel-page': isFunnelPage,
      'background--article-page': isArticlePage,
      'background--partner-page': isPartnerPage,
      'background--solid-color': solidColor
    }]"
  >
    <div
      v-if="!solidColor && background"
      class="images"
    >
      <template v-if="isHomePage && withTitleSpacer">
        <div class="image-1">
          <zg-image
            v-if="homePageBackgroundImage"
            :src="homePageBackgroundImage.src"
            :sources="homePageBackgroundImage.sources"
          />
        </div>

        <div class="spacer">
          <zg-image
            v-if="homePageBackgroundImageSpacer"
            :src="homePageBackgroundImageSpacer.src"
            :sources="homePageBackgroundImageSpacer.sources"
          />
        </div>

        <div class="image-2">
          <zg-image
            v-if="homePageBackgroundImage"
            :src="homePageBackgroundImage.src"
            :title="homePageBackgroundImage.title"
            :alt="homePageBackgroundImage.alt"
            :sources="homePageBackgroundImage.sources"
          />
        </div>
      </template>

      <zg-image
        v-else-if="backgroundImage"
        class="image"
        :src="backgroundImage.src"
        :title="backgroundImage.title"
        :alt="backgroundImage.alt"
        :sources="backgroundImage.sources"
      />
    </div>
  </div>
</template>

<script>
  import ZgImage from '../ZgImage/ZgImage'
  import backgrounds from './backgrounds'

  export default {
    name: 'Background',
    components: {
      ZgImage
    },
    props: {
      pageType: {
        type: String,
        required: true
      },
      isHomePage: {
        type: Boolean,
        required: false,
        default: false
      },
      isProductPage: {
        type: Boolean,
        required: false,
        default: false
      },
      isFunnelPage: {
        type: Boolean,
        required: false,
        default: false
      },
      isArticlePage: {
        type: Boolean,
        required: false,
        default: false
      },
      isPartnerPage: {
        type: Boolean,
        required: false,
        default: false
      },
      background: {
        type: Object,
        required: false,
        default: null
      },
      solidColor: {
        type: Boolean,
        default: false
      },
      withTitleSpacer: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data: () => ({
      imageTypes: ['webp', 'jpeg']
    }),
    computed: {
      backgroundImage () {
        // only remap CF images
        if (this.background?.src?.includes('ctfassets')) {
          const bg = this.background

          const sources = this.imageTypes.flatMap(type =>
            this.breakPoints.map(bp => ({
              media: bp.mq,
              srcsets: [
                `${bg.src}?h=${bp.heights[this.pageType] * 3}&q=70&fm=${type} 3x`,
                `${bg.src}?h=${bp.heights[this.pageType] * 2}&q=80&fm=${type} 2x`,
                `${bg.src}?h=${bp.heights[this.pageType]}&q=90&fm=${type} 1x`
              ],
              type: `image/${type}`
            })))

          return {
            ...bg,
            sources
          }
        }

        return this.background
      },
      homePageBackgroundImage () {
        return {
          src: backgrounds?.['on-your-side']?.src,
          sources: backgrounds?.['on-your-side']?.sources
        }
      },
      homePageBackgroundImageSpacer () {
        return {
          src: backgrounds?.['on-your-side-spacer']?.src,
          sources: backgrounds?.['on-your-side-spacer']?.sources
        }
      },
      breakPoints () {
        return [
          {
            mq: '(max-width: 767px)',
            heights: {
              'home-page': this.withTitleSpacer ? 424 : 224,
              'product-page': 208,
              'funnel-page': 208,
              'article-page': 208,
              'partner-page': 80
            }
          },
          {
            mq: '(max-width: 991px)',
            heights: {
              'home-page': 410,
              'product-page': 336,
              'funnel-page': 329,
              'article-page': 329,
              'partner-page': 80
            }
          },
          {
            mq: '(min-width: 992px)',
            heights: {
              'home-page': 596,
              'product-page': 596,
              'funnel-page': 400,
              'article-page': 800,
              'partner-page': 80
            }
          }
        ]
      }
    }
  }
</script>

<style lang="scss" scoped>
  .background {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    max-width: rem(4000px);
    margin: 0 auto;
    overflow: hidden;
    z-index: 0;

    .images {
      position: relative;

      ::v-deep picture {
        height: 100%;
        width: 100%;
      }

      ::v-deep img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // shared
  // ? ----------------------------------------------------------------------

  .background--home-page,
  .background--home-page-with-title-spacer,
  .background--product-page {
    .images::before {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      content: '';
      background-color: color(black, 0.2);
      z-index: 3;
    }
  }

  // ? ----------------------------------------------------------------------
  // home page
  // ? ----------------------------------------------------------------------

  .background--home-page {
    .images {
      width: rem(822px);
      height: rem(228px);

      @include mq(medium) {
        width: rem(1269px);
        height: rem(352px);
      }

      @include mq(large) {
        width: rem(2148px);
        height: rem(596px);
      }

      @include mq(2148px) {
        width: 100%;
      }
    }
  }

  .background--home-page-with-title-spacer {
    width: auto;
    height: rem(424px);

    @include mq(medium) {
      width: auto;
      height: rem(410px);
    }

    @include mq(large) {
      width: auto;
      height: rem(596px);
    }

    @include mq(2148px) {
      width: auto;
    }

    .images {
      height: rem(224px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      @include mq(medium) {
        height: rem(336px);
      }

      @include mq(large) {
        flex-direction: row;
        height: rem(596px);
      }

      ::v-deep picture {
        width: auto;
        height: 100%;
      }

      ::v-deep img {
        @include mq(large) {
          width: auto;
          max-width: unset;
          object-fit: unset;
        }
      }

      .image-1,
      .image-2 {
        flex-grow: 1;
        height: 100%;
        z-index: 2;

        ::v-deep img {
          object-position: top;
        }
      }

      .image-1 {
        display: none;

        @include mq(large) {
          display: block;
        }
      }

      .spacer {
        width: 100%;
        height: rem(148px);
        flex-shrink: 0;
        overflow: hidden;
        transform: scale(-100%);

        @include mq(medium) {
          height: rem(120px);
        }

        @include mq(large) {
          width: rem(560px);
          height: 100%;
          transform: none;
        }

        ::v-deep img {
          object-position: bottom;
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // product page
  // ? ----------------------------------------------------------------------

  .background--product-page {
    .images {
      width: rem(750px);
      height: rem(208px);

      @include mq(medium) {
        width: rem(1211px);
        height: rem(336px);
      }

      @include mq(large) {
        width: rem(2148px);
        height: rem(596px);
      }

      @include mq(2148px) {
        width: 100%;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // funnel page, article page, partner page
  // ? ----------------------------------------------------------------------

  .background--funnel-page,
  .background--article-page,
  .background--partner-page {
    background-color: color(brand-500);

    @include mq(large) {
      height: rem(240px);
    }

    &.background--with-background-image {
      background-color: transparent;

      @include mq(large) {
        height: auto;
      }

      .images {
        width: 100%;
        height: rem(145px) + rem(32px); // top margin of content box + radius

        @include mq(medium) {
          height: rem(256px) + rem(48px); // top margin of content box + radius
        }

        @include mq(large) {
          height: rem(297px) + rem(145px); // top margin of content box + extended visibility on the side of the content box
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // Solid color css
  // ? ----------------------------------------------------------------------

  .background--solid-color {
    background-color: color(brand-100);
  }
</style>
